import type { UseQueryResult } from "@tanstack/react-query";

import type { AssetAdoptionList } from "@/api/library";
import { formatDate } from "@/lib/format";
import { EmptyState, LoadingState } from "@/ui/states";

import { NamedReadFailureState } from "./named/NamedReadFailure";

/**
 * Which projects pin this asset, rendered before any lifecycle decision and on
 * the asset page.
 *
 * Projects are shared across every authorized user of this environment, so the
 * rows ARE the adoption set (amended #32, 2026-08-03): the count rendered here
 * is the size of the list below it, never a wider figure the reader cannot
 * enumerate. #202 B1 (PR #226) retired the contract's separate
 * `totalAdoptionCount`, so the payload can no longer even claim an impact the
 * list does not show. An archive decision has to be made against projects the
 * clinician can actually see.
 */
export function AdoptionImpact({
  adoptions,
  retry,
}: {
  adoptions: UseQueryResult<AssetAdoptionList>;
  /**
   * A cancel-then-refetch recovery for the adoptions key, built by the caller
   * with `useRetryPausedQuery` — never a bare `refetch`, which fires nothing
   * on a paused query (#215). The caller owns the key; this surface owns the
   * three-way choice about the failure.
   */
  retry: () => void;
}) {
  // A held read comes FIRST, before the cached rows it may be sitting on. A
  // decision-fresh refetch that failed and parked keeps `status: "success"`
  // with the previous rows in hand, and rendering those rows would put exactly
  // the stale account this surface exists to replace (PW-LIB-10, #648) in
  // front of a confirm control (PR #675 review). Whether the pause is the
  // connection or a server-answered failure, and whether a settled failure is
  // a denial (no retry can grant access) or transient, is one decision made
  // once in `NamedReadFailureState`.
  if (adoptions.isPaused) {
    return (
      <NamedReadFailureState
        failure={adoptions.failureReason}
        isPaused
        retry={retry}
        subject="Adoption impact"
        title="Adoption impact could not be loaded"
        description="The Editor Service did not return which projects pin this asset."
        deniedDescription="You are not authorized to read which projects pin this asset."
        offlineDescription="Which projects pin this asset could not be read. It loads on its own when the connection returns."
      />
    );
  }

  if (adoptions.isPending) {
    return <LoadingState label="Loading adoption impact" rows={2} />;
  }

  if (adoptions.isError) {
    return (
      <NamedReadFailureState
        failure={adoptions.error}
        isPaused={false}
        retry={retry}
        subject="Adoption impact"
        title="Adoption impact could not be loaded"
        description="The Editor Service did not return which projects pin this asset."
        deniedDescription="You are not authorized to read which projects pin this asset."
      />
    );
  }

  const { items } = adoptions.data;

  if (items.length === 0) {
    return (
      <EmptyState
        title="No project adopts this asset"
        description="Nothing pins any version of it yet."
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground">
        Adopted by {items.length} {items.length === 1 ? "project" : "projects"}{" "}
        in this environment.
      </p>
      <ul className="divide-y divide-border rounded-md border border-border">
        {items.map((adoption) => (
          <li
            key={adoption.projectId}
            className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm"
          >
            <span className="min-w-0 flex-1 font-medium text-foreground">
              {adoption.projectName}
            </span>
            <span className="font-mono text-foreground">{adoption.version}</span>
            <span className="text-muted-foreground">
              Pinned {formatDate(adoption.pinnedAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
